import { api } from "@/lib/api";

const base = "/api/vendor-portal";

export type VendorInvoiceStatus =
  | "DRAFT"
  | "PENDING_OPS"
  | "PENDING_ADMIN"
  | "REJECTED"
  | "APPROVED"
  | "PAID";

export type VendorScope = "HOSPITAL" | "ALL_HOSPITALS" | "CENTRAL";
export type VendorExpenseLedger = "CENTRAL" | "IPD_PATIENT";

export interface VendorCategory {
  id: string;
  hospitalId: string;
  name: string;
  expenseLedger?: VendorExpenseLedger;
  defaultExpenseCategory: string | null;
  defaultPatientExpenseCategory?: string | null;
  isActive: boolean;
}

export interface VendorApprovalStep {
  id?: string;
  stepOrder: number;
  assigneeType: "ROLE" | "STAFF" | "ADMIN";
  role?: string | null;
  staffId?: string | null;
  hospitalAdminId?: string | null;
  staff?: { id: string; name: string; role?: string; email?: string } | null;
  hospitalAdmin?: {
    id: string;
    name: string;
    email?: string;
    adminScope?: string;
    role?: string;
  } | null;
}

export type VendorApproverCandidate = {
  id: string;
  name: string;
  email: string | null;
  role: string;
  kind: "STAFF" | "ADMIN";
  adminScope?: string;
  label: string;
};

export interface HospitalVendor {
  id: string;
  hospitalId: string;
  categoryId: string | null;
  name: string;
  phone: string | null;
  email: string | null;
  gstNo: string | null;
  address: string | null;
  panNumber: string | null;
  staffId: string | null;
  scope?: VendorScope;
  requiresOpsApproval: boolean;
  isActive: boolean;
  notes: string | null;
  createdAt?: string;
  category?: VendorCategory | null;
  staff?: { id: string; name: string; email: string; status?: string } | null;
  approvalSteps?: VendorApprovalStep[];
  _count?: { invoices: number };
}

export interface VendorInvoice {
  id: string;
  hospitalId: string;
  vendorId: string;
  invoiceNumber: string;
  invoiceDate: string;
  amount: number;
  description: string | null;
  expenseLedger?: VendorExpenseLedger;
  expenseCategory: string | null;
  patientExpenseCategory?: string | null;
  expenseSubCategory: string | null;
  expenseHospitalId?: string | null;
  hospitalUnit?: string | null;
  serviceMonth?: string | null;
  admissionId?: string | null;
  currentStepIndex?: number;
  status: VendorInvoiceStatus;
  fileUrl: string | null;
  fileName: string | null;
  submittedAt: string | null;
  rejectReason: string | null;
  paidAt: string | null;
  vendor?: {
    id: string;
    name: string;
    requiresOpsApproval: boolean;
    scope?: VendorScope;
    category?: {
      id: string;
      name: string;
      expenseLedger?: VendorExpenseLedger;
      defaultExpenseCategory: string | null;
      defaultPatientExpenseCategory?: string | null;
    } | null;
    approvalSteps?: VendorApprovalStep[];
  };
  expenseHospital?: { id: string; name: string } | null;
  centralExpense?: {
    id: string;
    amount: number;
    workflowStatus: string;
    paymentStatus: string;
    paidAmount: number;
    paidAt: string | null;
    paymentMode: string | null;
    category: string;
    proofUrl: string | null;
  } | null;
  patientExpense?: {
    id: string;
    amount: number;
    workflowStatus: string;
    paymentStatus: string;
    paidAmount: number;
    paidAt: string | null;
    paymentMode: string | null;
    category: string;
    proofUrl: string | null;
  } | null;
  opsApprovedBy?: { id: string; name: string } | null;
  adminApprovedBy?: { id: string; name: string } | null;
  classifiedBy?: { id: string; name: string } | null;
}

export type ClassifyInvoiceBody = {
  expenseLedger?: string;
  expenseCategory?: string;
  patientExpenseCategory?: string;
  expenseSubCategory?: string;
  expenseHospitalId?: string;
  hospitalUnit?: string;
  serviceMonth?: string;
  admissionId?: string;
  description?: string;
  internalNotes?: string;
};

export const vendorPortalApi = {
  meta: () =>
    api.get<{
      data: {
        expenseCategories: string[];
        patientExpenseCategories: string[];
        expenseLedgers: string[];
        vendorScopes: string[];
        approvalRoles: string[];
        invoiceStatuses: string[];
        hospitalUnits: string[];
        paymentModes: string[];
      };
    }>(`${base}/meta`),

  listCategories: (params?: { includeInactive?: boolean; hospitalId?: string }) =>
    api.get<{ data: VendorCategory[] }>(`${base}/categories`, { params }),

  createCategory: (body: {
    name: string;
    expenseLedger?: string;
    defaultExpenseCategory?: string | null;
    defaultPatientExpenseCategory?: string | null;
    hospitalId?: string;
  }) => api.post<{ data: VendorCategory }>(`${base}/categories`, body),

  updateCategory: (
    id: string,
    body: {
      name?: string;
      expenseLedger?: string;
      defaultExpenseCategory?: string | null;
      defaultPatientExpenseCategory?: string | null;
      isActive?: boolean;
      hospitalId?: string;
    }
  ) => api.patch<{ data: VendorCategory }>(`${base}/categories/${id}`, body),

  listVendors: (params?: { activeOnly?: boolean; hospitalId?: string }) =>
    api.get<{ data: HospitalVendor[] }>(`${base}/vendors`, { params }),

  listApprovers: (params?: { hospitalId?: string }) =>
    api.get<{
      data: { staff: VendorApproverCandidate[]; admins: VendorApproverCandidate[] };
    }>(`${base}/approvers`, { params }),

  createVendor: (body: Record<string, unknown>) =>
    api.post<{ data: HospitalVendor }>(`${base}/vendors`, body),

  updateVendor: (id: string, body: Record<string, unknown>) =>
    api.patch<{ data: HospitalVendor }>(`${base}/vendors/${id}`, body),

  myProfile: () => api.get<{ data: HospitalVendor }>(`${base}/me`),

  listAdmissions: (params?: { q?: string; hospitalId?: string }) =>
    api.get<{
      data: Array<{
        id: string;
        admissionDate: string;
        status: string;
        surgeryName: string | null;
        queue: {
          patient: { id: string; name: string; uhid: string | null; phone: string };
        };
      }>;
    }>(`${base}/admissions`, { params }),

  listInvoices: (params?: {
    vendorId?: string;
    status?: string;
    from?: string;
    to?: string;
    hospitalId?: string;
  }) => api.get<{ data: VendorInvoice[] }>(`${base}/invoices`, { params }),

  getInvoice: (id: string) => api.get<{ data: VendorInvoice }>(`${base}/invoices/${id}`),

  createInvoice: (body: Record<string, unknown>) =>
    api.post<{ data: VendorInvoice }>(`${base}/invoices`, body),

  updateInvoice: (id: string, body: Record<string, unknown>) =>
    api.patch<{ data: VendorInvoice }>(`${base}/invoices/${id}`, body),

  submitInvoice: (id: string) => api.post<{ data: VendorInvoice }>(`${base}/invoices/${id}/submit`),

  uploadInvoiceFile: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post<{ data: VendorInvoice }>(`${base}/invoices/${id}/file`, fd);
  },

  opsApprove: (id: string, body?: ClassifyInvoiceBody) =>
    api.post<{ data: VendorInvoice }>(`${base}/invoices/${id}/ops-approve`, body || {}),

  approveStep: (id: string, body?: ClassifyInvoiceBody) =>
    api.post<{ data: VendorInvoice }>(`${base}/invoices/${id}/approve-step`, body || {}),

  setExpenseCategory: (id: string, body: ClassifyInvoiceBody) =>
    api.post<{ data: VendorInvoice }>(`${base}/invoices/${id}/set-category`, body),

  adminApprove: (id: string, body?: ClassifyInvoiceBody) =>
    api.post<{ data: VendorInvoice }>(`${base}/invoices/${id}/admin-approve`, body || {}),

  reject: (id: string, reason?: string) =>
    api.post<{ data: VendorInvoice }>(`${base}/invoices/${id}/reject`, { reason }),

  markPaid: (id: string, body: { paymentMode: string; paidAmount?: number }) =>
    api.post<{ data: { invoice: VendorInvoice } }>(`${base}/invoices/${id}/mark-paid`, body),
};
